import React, { useState, useEffect, useCallback, useRef } from 'react';
import { buildReceiptData, HistoryItem, ReceiptModal } from './HistoryShared';
import { copyToClipboard as copyToClipboardUtil } from '../utils/clipboard';

// Те же 16 цветов, что и PROMO_COLORS в Market.js/сервере (см. также
// server/routes/market.js) — 16 стандартных цветов красителей Minecraft:
// чёрный, белый, синий, коричневый, голубой, бирюзовый, зелёный,
// фиолетовый, жёлтый, красный, пурпурный, серый, оранжевый, светло-серый,
// лаймовый, розовый. Список приведён к единому виду с продвижением товаров,
// старый набор из 14 iOS-цветов убран.
const COLORS = ['#1d1d21', '#f9fffe', '#3c44aa', '#835432', '#3ab3da', '#169c9c', '#5e7c16', '#8932b8', '#fed83d', '#b02e26', '#c74ebd', '#474f52', '#f9801d', '#9d9d97', '#80c71f', '#f38baa'];
const MAX_ACCOUNTS = 4;
// Цвет по умолчанию для новых счетов — бирюзовый (совпадает с DEFAULT
// колонки account_color в БД, дублируем здесь для явного использования
// на клиенте и на случай отсутствия значения с сервера).
const DEFAULT_ACCOUNT_COLOR = '#169c9c';

// Текст на карточке счёта раньше был жёстко завязан на тему приложения
// (var(--text-on-accent)) — из-за этого на некоторых цветах карточки
// (например, белом или жёлтом фоне в светлой теме, чёрном или синем в
// тёмной) текст почти сливался с фоном. Вместо этого считаем контраст
// по фактическому цвету карточки (относительная яркость по WCAG) и
// выбираем тёмный или светлый текст независимо от темы.
function getReadableTextColor(hex) {
  if (!hex || hex[0] !== '#') return null;
  let c = hex.slice(1);
  if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
  if (c.length !== 6) return null;
  const r = parseInt(c.substr(0, 2), 16) / 255;
  const g = parseInt(c.substr(2, 2), 16) / 255;
  const b = parseInt(c.substr(4, 2), 16) / 255;
  const lin = v => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.5 ? '#14181a' : '#ffffff';
}

function Bank({ tgId, setActiveTab, onInitialLoad }) {
  const [userData, setUserData] = useState({ postalAddress: '', policyAccepted: false });
  const [accounts, setAccounts] = useState([]); // все счета игрока, включая удалённые (нужны для фильтра истории)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Состояния для модалки создания счёта (и первого, и любого следующего)
  const [createModal, setCreateModal] = useState(false);
  const [newAccountNum, setNewAccountNum] = useState('');
  const [newAccountName, setNewAccountName] = useState('');

  // Состояния для модалки перевода
  const [transferModal, setTransferModal] = useState(false);
  const [transferSearch, setTransferSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferComment, setTransferComment] = useState('');
  const [transferError, setTransferError] = useState('');
  // Вкладка модалки перевода: 'external' — на чужой счёт (как раньше),
  // 'internal' — между своими счетами. По умолчанию всегда открыта первая.
  const [transferTab, setTransferTab] = useState('external');
  const [transferOwnTarget, setTransferOwnTarget] = useState(null); // номер выбранного своего счёта-получателя

  // Состояния для модалки вывода
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawDest, setWithdrawDest] = useState('ACCOUNT');
  const [withdrawError, setWithdrawError] = useState('');

  // Состояния для модалки пополнения
  const [depositModal, setDepositModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositSourceType, setDepositSourceType] = useState('ACCOUNT');
  const [depositSourceValue, setDepositSourceValue] = useState('');
  const [depositProof, setDepositProof] = useState('');
  const [depositError, setDepositError] = useState('');

  // Состояния для истории
  const [history, setHistory] = useState([]);
  // Загрузилась ли история именно для текущего счёта. Пока false — не показываем
  // "История пуста": иначе при переключении между счетами (когда старый список уже
  // очищен, а новый ещё летит с сервера) на долю секунды мелькала эта надпись.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [filterType, setFilterType] = useState('ALL');
  const [filterAccount, setFilterAccount] = useState('ALL');

  // Состояния для настроек счета
  const [settingsModal, setSettingsModal] = useState(false);
  const [editAccountName, setEditAccountName] = useState('');
  const [editAccountColor, setEditAccountColor] = useState('');
  const [designPreview, setDesignPreview] = useState(''); // текущее/новое фото на карте (data URL)
  const [designFile, setDesignFile] = useState(null); // выбранное, но ещё не купленное фото
  const [designError, setDesignError] = useState('');
  const [designSubmitting, setDesignSubmitting] = useState(false);
  const [cardDesignPrice, setCardDesignPrice] = useState(16);
  
  // Состояния для удаления счета
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Состояние для чека
  const [receiptData, setReceiptData] = useState(null);
  const [invoicesModal, setInvoicesModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const historyRequestId = useRef(0);
  const carouselRef = useRef(null);
  const pendingScrollIndex = useRef(null);
  const lastPersistedAccountRef = useRef(null);
  const wheelAccum = useRef(0);
  const wheelLocked = useRef(false);
  const wheelUnlockTimeout = useRef(null);
  const wheelIdleTimeout = useRef(null);
  const touchStartX = useRef(0);
  const prevLoopRef = useRef(false);

  const visibleAccounts = accounts.filter(a => a.status !== 'DELETED');
  const canAddMore = visibleAccounts.length < MAX_ACCOUNTS;
  const ringLength = visibleAccounts.length + (canAddMore ? 1 : 0);

  // Карусель полностью на transform: translateX (как уже работающая карусель рекламы
  // в Market.js), без единого обращения к нативному scrollLeft/scroll-snap. trackIndex —
  // непрерывная позиция в extendedSlides, двигается строго на 1 за одно действие (тик
  // колеса, свайп, клик по точке) — раньше это отдавалось на откуп браузерной инерции
  // скролла, из-за чего одно действие иногда пролистывало сразу несколько карточек, а
  // "оживала" карусель только после первого программного scrollTo (клик по точке).
  // Логический индекс счёта (cardIndex) больше не отдельное состояние, а всегда выводится
  // из trackIndex — рассинхрон между тем, что видно, и тем, что выбрано, теперь невозможен.
  const loop = ringLength > 1;
  const baseSlides = [
    ...visibleAccounts.map(acc => ({ kind: 'account', acc })),
    ...(canAddMore ? [{ kind: 'placeholder' }] : [])
  ];
  const extendedSlides = loop ? [baseSlides[baseSlides.length - 1], ...baseSlides, baseSlides[0]] : baseSlides;

  const [trackIndex, setTrackIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const cardIndex = loop
    ? (((trackIndex - 1) % ringLength) + ringLength) % ringLength
    : Math.max(0, Math.min(ringLength - 1, trackIndex));

  const isPlaceholderCard = canAddMore && cardIndex === visibleAccounts.length;
  const currentAccount = isPlaceholderCard ? null : visibleAccounts[cardIndex];

  const goToIndex = useCallback((idx, animate = true) => {
    if (ringLength === 0) return;
    setIsTransitioning(animate);
    setTrackIndex(loop ? idx + 1 : idx);
  }, [ringLength, loop]);

  const nextCard = useCallback(() => {
    if (ringLength <= 1) return;
    setIsTransitioning(true);
    setTrackIndex(prev => Math.min(prev + 1, extendedSlides.length - 1));
  }, [ringLength, extendedSlides.length]);

  const prevCard = useCallback(() => {
    if (ringLength <= 1) return;
    setIsTransitioning(true);
    setTrackIndex(prev => Math.max(prev - 1, 0));
  }, [ringLength]);

  // Долистали (свайпом/колесом) до клона на одном из краёв ленты — мгновенно, без анимации,
  // перепрыгиваем на настоящую карточку с той же стороны. Снаружи это выглядит как честный
  // бесшовный цикл в обе стороны, а не рывок назад через всю ленту.
  const handleTrackTransitionEnd = () => {
    if (!loop) return;
    if (trackIndex === 0) {
      setIsTransitioning(false);
      setTrackIndex(ringLength);
    } else if (trackIndex === extendedSlides.length - 1) {
      setIsTransitioning(false);
      setTrackIndex(1);
    }
  };

  // Держим индекс в допустимых границах, если список счетов изменился (счёт создан/удалён)
  useEffect(() => {
    if (ringLength > 0 && cardIndex >= ringLength) {
      goToIndex(ringLength - 1, false);
    }
  }, [ringLength, cardIndex, goToIndex]);

  // Если закольцованность включилась или выключилась (был 1 счёт — стало 2, или наоборот),
  // смещение позиции в ленте (клон в начале) меняется — пересчитываем позицию без анимации.
  useEffect(() => {
    if (prevLoopRef.current !== loop) {
      prevLoopRef.current = loop;
      goToIndex(cardIndex, false);
    }
  }, [loop, cardIndex, goToIndex]);

  // Прокрутка колесиком мыши/трекпадом: один "тик" (или суммарный сдвиг выше порога) —
  // переключение ровно на одну карточку, с небольшой "перезарядкой" между переключениями,
  // чтобы инерция колеса/трекпада не пролистывала сразу несколько карточек подряд. Слушатель
  // навешивается сразу при монтировании и работает с первого же тика — раньше в похожей
  // схеме карусель "оживала" только после клика по точке, здесь такой зависимости нет.
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    // Порог суммарного смещения колеса/трекпада, при котором засчитывается один шаг
    // карусели. Трекпад присылает много мелких wheel-событий за один жест — копим их
    // в wheelAccum, а не реагируем на первое же событие, иначе один жест листал бы
    // сразу несколько карточек.
    const WHEEL_THRESHOLD = 40;
    // Блокировка новых шагов держится, пока не доиграет CSS-переход трека (0.35s) —
    // с запасом, чтобы не поймать статус "между карточками" при следующем шаге.
    const WHEEL_LOCK_MS = 420;
    // Если жест закончился, а порог набрать не успели — сбрасываем накопленное через
    // паузу, иначе бесхозный остаток сложится со следующим никак не связанным жестом.
    const WHEEL_IDLE_MS = 150;

    const onWheel = (e) => {
      if (ringLength <= 1) return;
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;
      e.preventDefault();
      if (wheelLocked.current) return;

      wheelAccum.current += delta;

      if (wheelIdleTimeout.current) clearTimeout(wheelIdleTimeout.current);
      wheelIdleTimeout.current = setTimeout(() => { wheelAccum.current = 0; }, WHEEL_IDLE_MS);

      if (Math.abs(wheelAccum.current) < WHEEL_THRESHOLD) return;

      const goNext = wheelAccum.current > 0;
      wheelAccum.current = 0;
      wheelLocked.current = true;
      if (goNext) nextCard(); else prevCard();
      if (wheelUnlockTimeout.current) clearTimeout(wheelUnlockTimeout.current);
      wheelUnlockTimeout.current = setTimeout(() => { wheelLocked.current = false; }, WHEEL_LOCK_MS);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (wheelUnlockTimeout.current) clearTimeout(wheelUnlockTimeout.current);
      if (wheelIdleTimeout.current) clearTimeout(wheelIdleTimeout.current);
    };
  // loading и showFullHistory добавлены в зависимости по одной и той же причине: карусель —
  // это конкретный DOM-узел (carouselRef.current), а не постоянно существующий элемент.
  // Пока идёт загрузка, вместо неё рендерится плейсхолдер "Загрузка...", а при открытии
  // вкладки "Вся история" карусель вообще размонтируется (см. `if (showFullHistory) return`
  // ниже) — в обоих случаях carouselRef.current становится null, и эффект выходит по
  // `if (!el) return`. Без этих зависимостей эффект не перезапускался бы повторно после того,
  // как карусель снова примонтируется с НОВЫМ DOM-узлом — ringLength/nextCard/prevCard к этому
  // моменту могли не измениться, и слушатель колеса так и остался бы не навешен ни на один
  // актуальный узел. Из-за этого после возврата из "Всей истории" прокрутка карусели колесом
  // переставала работать (свайпы работали исправно, т.к. это обычные React-обработчики в JSX,
  // навешиваются заново при каждом рендере — в отличие от addEventListener в эффекте).
  }, [ringLength, nextCard, prevCard, loading, showFullHistory]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (ringLength <= 1) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (diff > 40) nextCard();
    else if (diff < -40) prevCard();
  };

  // Применяем отложенный переход на карточку после того, как она реально появится в DOM
  // (например, сразу после создания нового счёта)
  useEffect(() => {
    if (pendingScrollIndex.current !== null && pendingScrollIndex.current < ringLength) {
      const idx = pendingScrollIndex.current;
      pendingScrollIndex.current = null;
      goToIndex(idx, false);
    }
  }, [ringLength, goToIndex]);

  // Как только счета полностью загрузились при открытии вкладки — сразу показываем
  // тот счёт, который сейчас выбран как активный (а не первый попавшийся). Раньше
  // карусель могла остаться там, где оказалась, пока ещё считала, что счетов нет
  // (доля секунды до ответа сервера), и тогда видимая карточка расходилась
  // с реально показанными данными (например, историей).
  useEffect(() => {
    if (!loading && visibleAccounts.length > 0) {
      const selectedIdx = visibleAccounts.findIndex(a => a.is_selected);
      const idx = selectedIdx >= 0 ? selectedIdx : 0;
      lastPersistedAccountRef.current = visibleAccounts[idx].account_number;
      goToIndex(idx, false);
    }
  }, [loading]);

  // Карточка, на которой сейчас остановилась карусель, становится "активным" счётом
  // игрока для всего приложения — с неё будут списываться покупки, оплата инвойсов
  // и т.д., и её баланс будет показан в шапке. Сохраняем это на сервере, как только
  // карусель реально остановилась на новой карточке (пока не при каждом кадре свайпа).
  useEffect(() => {
    if (loading || isPlaceholderCard || !currentAccount) return;
    if (lastPersistedAccountRef.current === currentAccount.account_number) return;
    lastPersistedAccountRef.current = currentAccount.account_number;
    fetch('/api/bank/select-account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tg_id: tgId, account_number: currentAccount.account_number })
    }).catch(() => {});
  }, [loading, isPlaceholderCard, currentAccount?.account_number, tgId]);

  const copyToClipboard = (text) => {
    copyToClipboardUtil(text, () => showToastMsg('Скопировано'), () => showToastMsg('Ошибка копирования', true));
  };

  const fetchUserData = useCallback(async () => {
    try {
      const res = await fetch(`/api/bank/${tgId}`);
      const json = await res.json();
      setUserData(prev => {
        const next = { postalAddress: json.postal_address || '', policyAccepted: json.policy_accepted || false };
        if (prev.postalAddress !== next.postalAddress || prev.policyAccepted !== next.policyAccepted) return next;
        return prev;
      });
    } catch (err) {
      console.error('Ошибка загрузки банка:', err);
    }
  }, [tgId]);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await fetch(`/api/bank/${tgId}/accounts`);
      const json = await res.json();
      const next = json.accounts || [];
      setAccounts(prev => (JSON.stringify(prev) !== JSON.stringify(next) ? next : prev));
      return next;
    } catch (err) {
      console.error('Ошибка загрузки счетов:', err);
      return [];
    }
  }, [tgId]);

  const fetchHistory = useCallback(async (page, limit, accountNum, type) => {
    const requestId = ++historyRequestId.current;
    try {
      const res = await fetch(`/api/bank/history/${tgId}?page=${page}&limit=${limit}&type=${type}&account=${accountNum}`);
      const json = await res.json();
      if (requestId !== historyRequestId.current) return; // пришёл устаревший ответ — более новый запрос уже в пути или уже получен
      setHistory(prev => {
        const newHistory = json.history || [];
        if (JSON.stringify(prev) !== JSON.stringify(newHistory)) return newHistory;
        return prev;
      });
      setHistoryLoaded(true);
    } catch (err) {
      console.error('Ошибка загрузки истории:', err);
    }
  }, [tgId]);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await fetch(`/api/bank/invoices/${tgId}`);
      const json = await res.json();
      setInvoices(prev => {
        const newInvoices = json.invoices || [];
        if (JSON.stringify(prev) !== JSON.stringify(newInvoices)) return newInvoices;
        return prev;
      });
    } catch (err) {
      console.error('Ошибка загрузки штрафов:', err);
    }
  }, [tgId]);

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      await fetchUserData();
      await fetchAccounts();
      await fetchInvoices();
      if (isMounted) { setLoading(false); onInitialLoad && onInitialLoad(); }
    };
    init();
    fetch('/api/admin/settings').then(r => r.json()).then(data => {
      if (isMounted && data.card_design_price) setCardDesignPrice(data.card_design_price);
    }).catch(() => {});
    const interval = setInterval(() => {
      fetchUserData();
      fetchAccounts();
      fetchInvoices();
    }, 1500);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [fetchUserData, fetchAccounts, fetchInvoices]);

  // Компактная история — только для текущей карточки. Важно: зависим от currentAccount?.id
  // (стабильное примитивное значение), а не от самого объекта currentAccount — он
  // пересоздаётся заново при каждом опросе счетов с сервера (даже если реально
  // ничего не изменилось), и раньше это заставляло историю дважды перезагружаться
  // при каждом переключении карточки.
  useEffect(() => {
    if (!currentAccount || showFullHistory) return;
    setHistoryLoaded(false);
    setHistory([]);
    fetchHistory(1, 5, currentAccount.id, 'ALL');
    const interval = setInterval(() => fetchHistory(1, 5, currentAccount.id, 'ALL'), 1500);
    return () => clearInterval(interval);
  }, [currentAccount?.id, showFullHistory, fetchHistory]);

  // Полная история — с учётом выбранных фильтров
  useEffect(() => {
    if (!showFullHistory) return;
    setHistoryLoaded(false);
    fetchHistory(historyPage, 50, filterAccount, filterType);
    const interval = setInterval(() => fetchHistory(historyPage, 50, filterAccount, filterType), 1500);
    return () => clearInterval(interval);
  }, [showFullHistory, historyPage, filterType, filterAccount, fetchHistory]);

  useEffect(() => {
    if (transferSearch.trim() === '' || selectedRecipient) {
      setSearchResults([]);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      try {
        const res = await fetch(`/api/bank/search/recipient?q=${encodeURIComponent(transferSearch)}&tg_id=${tgId}`);
        const json = await res.json();
        setSearchResults(json.results || []);
      } catch (err) {
        console.error('Ошибка поиска:', err);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [transferSearch, selectedRecipient, tgId]);

  useEffect(() => {
    if (depositSourceType === 'ACCOUNT') {
      setDepositSourceValue(currentAccount ? String(currentAccount.account_number) : '');
    } else {
      setDepositSourceValue(userData.postalAddress || '');
    }
  }, [depositSourceType, currentAccount, userData.postalAddress]);

  const showToastMsg = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAcceptPolicy = async () => {
    try {
      await fetch('/api/bank/accept-policy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId })
      });
      setUserData(prev => ({ ...prev, policyAccepted: true }));
    } catch (err) {
      console.error('Ошибка принятия политики', err);
    }
  };

  const handleCreateAccount = async () => {
    setError('');
    if (newAccountNum === '') return setError('Введите номер счета');
    if (newAccountName.trim() === '') return setError('Введите название счета');
    
    try {
      const res = await fetch('/api/bank/accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, account_number: newAccountNum, account_name: newAccountName })
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || 'Ошибка создания счета');
      } else {
        setCreateModal(false);
        setNewAccountNum('');
        setNewAccountName('');
        const next = await fetchAccounts();
        const idx = next.filter(a => a.status !== 'DELETED').findIndex(a => a.account_number === json.account_number);
        if (idx >= 0) pendingScrollIndex.current = idx;
      }
    } catch (err) {
      setError('Ошибка соединения с сервером');
    }
  };

  const handleUpdateAccount = async () => {
    if (!currentAccount) return;
    if (editAccountName.trim() === currentAccount.account_name && editAccountColor === currentAccount.account_color) {
      setSettingsModal(false);
      return;
    }

    try {
      const res = await fetch(`/api/bank/accounts/${currentAccount.account_number}/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, account_name: editAccountName, account_color: editAccountColor })
      });
      if (res.ok) {
        setSettingsModal(false);
        fetchAccounts();
      } else {
        showToastMsg((await res.json()).error, true);
      }
    } catch (err) {
      console.error('Ошибка обновления счета', err);
    }
  };

  const handleDesignFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      setDesignError('Файл слишком большой (макс 3МБ)');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setDesignFile(reader.result);
      setDesignPreview(reader.result);
      setDesignError('');
    };
    reader.readAsDataURL(file);
  };

  const handlePurchaseDesign = async () => {
    if (!currentAccount || !designFile || designSubmitting) return;
    setDesignSubmitting(true);
    setDesignError('');
    try {
      const res = await fetch(`/api/bank/accounts/${currentAccount.account_number}/card-design`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, image_data: designFile })
      });
      const data = await res.json();
      if (!res.ok) {
        setDesignError(data.error || 'Ошибка покупки дизайна');
      } else {
        setDesignFile(null);
        showToastMsg('Дизайн карты применён!');
        fetchAccounts();
      }
    } catch (err) {
      setDesignError('Ошибка соединения с сервером');
    } finally {
      setDesignSubmitting(false);
    }
  };

  const handleRemoveDesign = async () => {
    if (!currentAccount) return;
    try {
      const res = await fetch(`/api/bank/accounts/${currentAccount.account_number}/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, account_name: editAccountName, account_color: editAccountColor, remove_image: true })
      });
      if (res.ok) {
        setDesignPreview('');
        setDesignFile(null);
        showToastMsg('Фото убрано');
        fetchAccounts();
      } else {
        showToastMsg((await res.json()).error, true);
      }
    } catch (err) {
      console.error('Ошибка удаления дизайна', err);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentAccount) return;
    try {
      const res = await fetch(`/api/bank/accounts/${currentAccount.account_number}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId })
      });
      const json = await res.json();
      if (!res.ok) {
        showToastMsg(json.error, true);
      } else {
        setDeleteModal(false);
        setDeleteConfirmText('');
        showToastMsg(json.movedToAccount ? `Счёт удалён, остаток переведён на счёт #${json.movedToAccount}` : 'Счёт удалён');
        const next = await fetchAccounts();
        const stillLive = next.filter(a => a.status !== 'DELETED');
        const selectedIdx = stillLive.findIndex(a => a.is_selected);
        pendingScrollIndex.current = selectedIdx >= 0 ? selectedIdx : 0;
      }
    } catch (err) {
      console.error('Ошибка удаления счета', err);
    }
  };

  const handleTransfer = async () => {
    if (isSubmitting || !currentAccount) return;
    setTransferError('');
    const isInternal = transferTab === 'internal';
    if (isInternal) {
      if (!transferOwnTarget) return setTransferError('Выберите счёт для перевода');
    } else {
      if (!transferSearch) return setTransferError('Укажите получателя');
    }
    if (!transferAmount || transferAmount <= 0) return setTransferError('Укажите корректную сумму');

    // Перевод между своими — это перевод по номеру своего же счёта (recipient_type='account').
    const rType = isInternal ? 'account' : (selectedRecipient ? selectedRecipient.type : (transferSearch.match(/^\d+$/) ? 'account' : 'nickname'));
    const rValue = isInternal ? transferOwnTarget : (selectedRecipient ? selectedRecipient.value : transferSearch);

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/bank/transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          sender_tg_id: tgId,
          from_account: currentAccount.account_number,
          recipient_type: rType,
          recipient_value: rValue, 
          amount: transferAmount,
          comment: isInternal ? '' : transferComment
        })
      });
      const json = await res.json();
      if (!res.ok) {
        setTransferError(json.error || 'Ошибка перевода');
      } else {
        // Мгновенно правим баланс на карточке(ах), не дожидаясь ответа fetchAccounts —
        // сумма на счёте меняется сразу после перевода, а следующий опрос сервера
        // просто подтверждает уже показанное значение.
        const amtNum = parseInt(transferAmount, 10) || 0;
        const fromNum = String(currentAccount.account_number);
        const toOwnNum = isInternal ? String(transferOwnTarget) : null;
        setAccounts(prev => prev.map(a => {
          if (String(a.account_number) === fromNum) return { ...a, balance: (a.balance || 0) - amtNum };
          if (toOwnNum && String(a.account_number) === toOwnNum) return { ...a, balance: (a.balance || 0) + amtNum };
          return a;
        }));
        setTransferModal(false);
        setTransferSearch('');
        setTransferAmount('');
        setTransferComment('');
        setSelectedRecipient(null);
        setTransferTab('external');
        setTransferOwnTarget(null);
        fetchAccounts();
        fetchHistory(1, 5, currentAccount.id, 'ALL');
        showToastMsg('Успешный перевод!');
      }
    } catch (err) {
      setTransferError('Ошибка соединения с сервером');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (isSubmitting || !currentAccount) return;
    setWithdrawError('');
    if (!withdrawAmount || withdrawAmount <= 0) return setWithdrawError('Укажите корректную сумму');
    
    const destValue = withdrawDest === 'MAIL' ? userData.postalAddress : currentAccount.account_number;

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/bank/withdraw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          tg_id: tgId,
          account_number: currentAccount.account_number,
          amount: withdrawAmount,
          destination_type: withdrawDest,
          destination_value: destValue
        })
      });
      const json = await res.json();
      if (!res.ok) {
        setWithdrawError(json.error || 'Ошибка вывода');
      } else {
        setWithdrawModal(false);
        setWithdrawAmount('');
        fetchAccounts();
        showToastMsg('Заявление на вывод успешно подано!');
      }
    } catch (err) {
      setWithdrawError('Ошибка соединения с сервером');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDepositFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setDepositError('Файл слишком большой (макс 2МБ)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setDepositProof(reader.result);
        setDepositError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeposit = async () => {
    if (isSubmitting || !currentAccount) return;
    setDepositError('');
    if (!depositAmount || depositAmount <= 0) return setDepositError('Укажите корректную сумму');
    if (!depositSourceValue || depositSourceValue.trim() === '') return setDepositError('Укажите реквизиты отправителя');

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/bank/deposit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          tg_id: tgId,
          account_number: currentAccount.account_number,
          amount: depositAmount,
          source_type: depositSourceType,
          source_value: depositSourceValue,
          proof_image: depositProof
        })
      });
      const json = await res.json();
      if (!res.ok) {
        setDepositError(json.error || 'Ошибка пополнения');
      } else {
        setDepositModal(false);
        setDepositAmount('');
        setDepositSourceValue('');
        setDepositProof('');
        fetchHistory(1, 5, currentAccount.id, 'ALL');
        showToastMsg('Заявление на пополнение подано!');
      }
    } catch (err) {
      setDepositError('Ошибка соединения с сервером');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      const res = await fetch('/api/bank/invoices/pay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, invoice_id: invoiceId })
      });
      const json = await res.json();
      if (!res.ok) {
        showToastMsg(json.error || 'Ошибка оплаты', true);
      } else {
        showToastMsg('Инвойс успешно оплачен!');
        fetchAccounts();
        fetchInvoices();
        if (currentAccount) fetchHistory(1, 5, currentAccount.id, 'ALL');
      }
    } catch (err) {
      showToastMsg('Ошибка соединения с сервером', true);
    }
  };

  const openReceipt = (item) => {
    const accNumber = !showFullHistory
      ? (currentAccount ? currentAccount.account_number : undefined)
      : (filterAccount !== 'ALL' ? (accounts.find(a => String(a.id) === String(filterAccount)) || {}).account_number : undefined);
    setReceiptData(buildReceiptData(item, tgId, accNumber));
  };

  if (loading) return <div className="card" style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Загрузка...</div>;

  if (!userData.policyAccepted) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '30px 20px' }}>
        <h2>Политика банка</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px' }}>
          Для использования Azon банка необходимо ознакомиться и принять условия.
        </p>
        <button className="link-btn" onClick={() => setActiveTab('policy')} style={{ marginBottom: '20px', display: 'block', margin: '0 auto 20px auto' }}>
          Пользовательское соглашение
        </button>
        <button className="primary-btn" onClick={handleAcceptPolicy}>
          Принять и продолжить
        </button>
      </div>
    );
  }

  // Совсем нет счетов — показываем полноэкранную форму создания первого счёта
  if (visibleAccounts.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '30px 20px' }}>
        <h2>Создание счета</h2>
        <div className="account-num-notice">
          <span className="icon">⚠️</span>
          <span><b>Важно:</b> указывайте номер именно вашего счёта с сервера — не придумывайте случайное число. Эти данные привязываются навсегда.</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px' }}>
          Введите номер вашего счета с сервера (от 0 до 9999) и придумайте ему название.
        </p>
        <input 
          type="text" 
          inputMode="numeric"
          value={newAccountNum || ''} 
          onChange={(e) => setNewAccountNum(e.target.value.replace(/\D/g, '').slice(0, 4))} 
          placeholder="Номер счета (например: 1234)"
        />
        <input 
          type="text" 
          value={newAccountName || ''} 
          onChange={(e) => setNewAccountName(e.target.value)} 
          placeholder="Название (например: Копилка)"
        />
        <button className="primary-btn" onClick={handleCreateAccount} style={{ marginTop: '10px' }}>
          Создать счет
        </button>
        {error && <div style={{color: 'var(--danger)', marginTop: '10px'}}>{error}</div>}
      </div>
    );
  }

  const isBlocked = currentAccount && currentAccount.status === 'BLOCKED';
  // Со счёта казны доступен только перевод — заявки на пополнение и вывод по нему не подаются.
  const isTreasuryCard = !!(currentAccount && currentAccount.is_treasury);
  // Счета-получатели для перевода "между своими": все активные счета игрока,
  // кроме текущего (с него списываем) и кроме казны — казна не считается своим
  // счётом-получателем, хотя с неё (как с текущего) переводить на свой можно.
  const ownTransferTargets = currentAccount
    ? visibleAccounts.filter(a => a.account_number !== currentAccount.account_number && !a.is_treasury && a.status === 'ACTIVE')
    : [];

  if (showFullHistory) {
    return (
      <div>
        <button className="secondary-btn" onClick={() => {
          setShowFullHistory(false);
          setFilterType('ALL');
          setFilterAccount(currentAccount ? String(currentAccount.id) : 'ALL');
          setHistoryPage(1);
        }} style={{marginBottom: '15px', width: 'auto', padding: '10px 15px'}}>← Назад в банк</button>
        <h2 style={{marginTop: 0}}>История операций</h2>
        
        <div className="history-filters">
          <select value={filterType} onChange={e => { setFilterType(e.target.value); setHistoryPage(1); window.scrollTo(0,0); }}>
            <option value="ALL">Все операции</option>
            <option value="TRANSFER_OUT">Все списания</option>
            <option value="TRANSFER_IN">Все поступления</option>
            <option value="CREATE_ACCOUNT">Создание счета</option>
            <option value="UPDATE_ACCOUNT">Изменение настроек</option>
            <option value="DELETE_ACCOUNT">Удаление счета</option>
          </select>
          <select value={filterAccount} onChange={e => { setFilterAccount(e.target.value); setHistoryPage(1); window.scrollTo(0,0); }}>
            <option value="ALL">Все счета</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>
                {a.is_treasury ? 'Казна' : 'Счет'} #{a.account_number}{a.status === 'DELETED' ? ' (удалён)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="card">
          {history.length === 0 ? (historyLoaded ? <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>История пуста</p> : null) : history.map(item => (
            <HistoryItem
              key={item.unique_id}
              item={item}
              currentTgId={tgId}
              currentAccountNumber={filterAccount !== 'ALL' ? (accounts.find(a => String(a.id) === String(filterAccount)) || {}).account_number : undefined}
              isDeletedAccountOp={!visibleAccounts.some(a => a.account_number === item.from_account || a.account_number === item.to_account)}
              onReceiptClick={openReceipt}
              onPayInvoice={handlePayInvoice}
            />
          ))}
        </div>

        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
          <button className="secondary-btn" style={{width: 'auto', padding: '10px 20px'}} disabled={historyPage === 1} onClick={() => { setHistoryPage(p => p - 1); window.scrollTo(0,0); }}>←</button>
          <span style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Страница {historyPage}</span>
          <button className="secondary-btn" style={{width: 'auto', padding: '10px 20px'}} disabled={history.length < 50} onClick={() => { setHistoryPage(p => p + 1); window.scrollTo(0,0); }}>→</button>
        </div>

      <ReceiptModal receiptData={receiptData} onClose={() => setReceiptData(null)} onCopyReceipt={copyToClipboard} />
      </div>
    );
  }

  return (
    <div>
      {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.msg}</div>}

      <div className="account-carousel-viewport" ref={carouselRef} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div
          className="account-carousel-track"
          style={{ transform: `translateX(calc(-${trackIndex * 86}% + 7%))`, transition: isTransitioning ? 'transform 0.35s ease-in-out' : 'none' }}
          onTransitionEnd={handleTrackTransitionEnd}
        >
          {extendedSlides.map((slide, extIdx) => (
            <div
              key={extIdx}
              className={`account-carousel-slide ${extIdx === trackIndex ? 'active' : ''}`}
              onClick={() => {
                // Клик по торчащему краю соседней (неактивной) карточки — перелистываем на неё.
                // Внутренние кнопки активной карточки не задеты: pointer-events на них не отключён
                // (см. CSS), поэтому их клики сюда вообще не всплывают своим действием, а этот
                // обработчик для активной карточки — no-op.
                if (extIdx === trackIndex) return;
                setIsTransitioning(true);
                setTrackIndex(extIdx);
              }}
            >
              {slide.kind === 'placeholder' ? (
                <div className="account-card placeholder" onClick={() => setCreateModal(true)}>
                  <div className="account-card-inner">
                    <span className="account-card-plus">+</span>
                    <span>Создать счёт</span>
                  </div>
                </div>
              ) : (
                (() => {
                  const acc = slide.acc;
                  const blocked = acc.status === 'BLOCKED';
                  // Счёт казны оформляется логотипом приложения, а его название, цвет,
                  // фото и удаление недоступны — поэтому кнопки "Настроить" на нём нет.
                  const isTreasury = !!acc.is_treasury;
                  const hasImage = isTreasury || !!acc.card_image_url;
                  // Кнопка «Настроить» раньше всегда была полупрозрачно-белой — на светлых
                  // цветах карточки (белый, жёлтый и т.п.) она сливалась с фоном. Подбираем
                  // контрастные фон/текст кнопки по той же логике, что и текст карточки.
                  const cardTextColor = !hasImage ? getReadableTextColor(acc.account_color) : null;
                  const settingsBtnVars = cardTextColor === '#14181a'
                    ? { '--settings-btn-bg': 'rgba(20,24,26,0.45)', '--settings-btn-color': '#ffffff', '--settings-btn-shadow': '0 1px 2px rgba(0,0,0,0.4)' }
                    : cardTextColor === '#ffffff'
                      ? { '--settings-btn-bg': 'rgba(255,255,255,0.32)', '--settings-btn-color': '#14181a', '--settings-btn-shadow': 'none' }
                      : {};
                  const cardStyle = isTreasury
                    ? { backgroundImage: `url(${process.env.PUBLIC_URL}/logo_treasury.png)` }
                    : hasImage
                      ? { backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.35)), url(${acc.card_image_url})` }
                      : { background: acc.account_color, '--card-text-color': cardTextColor || undefined, ...settingsBtnVars };
                  return (
                    <div className={`account-card ${blocked ? 'blocked' : ''} ${hasImage ? 'has-image' : ''} ${isTreasury ? 'treasury' : ''}`} style={cardStyle}>
                      <div className="account-card-inner">
                        {blocked && <div className="status-badge" style={{color: 'var(--danger)'}}>ЗАБЛОКИРОВАН</div>}

                        {!blocked && !isTreasury && (
                          <button className="settings-btn" onClick={() => {
                            setEditAccountName(acc.account_name);
                            setEditAccountColor(acc.account_color);
                            setDesignPreview(acc.card_image_url || '');
                            setDesignFile(null);
                            setSettingsModal(true);
                          }}>Настроить</button>
                        )}

                        <h3>{acc.account_name} #{acc.account_number}</h3>
                        <p className="account-balance">{acc.balance} сц</p>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          ))}
        </div>
      </div>

      {ringLength > 1 && (
        <div className="account-carousel-dots">
          {Array.from({ length: ringLength }).map((_, idx) => (
            <span key={idx} className={`account-carousel-dot ${idx === cardIndex ? 'active' : ''}`} onClick={() => goToIndex(idx)} />
          ))}
        </div>
      )}

      {!isPlaceholderCard && currentAccount && (
        <>
          <div className="bank-actions">
            {!isTreasuryCard && <button className="action-btn" onClick={() => isBlocked ? showToastMsg('Счет заблокирован, операция невозможна', true) : setDepositModal(true)}>Пополнить</button>}
            <button className="action-btn" onClick={() => { if (isBlocked) return showToastMsg('Счет заблокирован, операция невозможна', true); setTransferTab('external'); setTransferOwnTarget(null); setTransferModal(true); }}>Перевести</button>
            {!isTreasuryCard && <button className="action-btn" onClick={() => isBlocked ? showToastMsg('Счет заблокирован, операция невозможна', true) : setWithdrawModal(true)}>Вывести</button>}
          </div>

          <div className="card">
            <div className="history-header-row">
              <h3>Последние действия</h3>
            </div>
            <div>
              {history.length === 0 ? (
                historyLoaded ? <p style={{color: 'var(--text-secondary)', fontSize: '14px'}}>История пуста</p> : null
              ) : (
                history.map(item => (
                  <HistoryItem
                    key={item.unique_id}
                    item={item}
                    currentTgId={tgId}
                    currentAccountNumber={currentAccount ? currentAccount.account_number : undefined}
                    isDeletedAccountOp={false}
                    onReceiptClick={openReceipt}
                    onPayInvoice={handlePayInvoice}
                  />
                ))
              )}
            </div>
            {history.length > 0 && (
              <button className="secondary-btn" style={{marginTop: '15px'}} onClick={() => { setFilterAccount(String(currentAccount.id)); setShowFullHistory(true); }}>
                Показать всё
              </button>
            )}
          </div>
        </>
      )}

      {invoices.length > 0 && (
        <button className="floating-invoice-btn" onClick={() => setInvoicesModal(true)}>
          🧾 Инвойсы <span className="badge">{invoices.length}</span>
        </button>
      )}

      <div style={{textAlign: 'center', marginTop: '20px', marginBottom: '10px'}}>
        <button className="link-btn" onClick={() => setActiveTab('policy')}>Пользовательское соглашение</button>
      </div>

      <ReceiptModal receiptData={receiptData} onClose={() => setReceiptData(null)} onCopyReceipt={copyToClipboard} />

      {/* Модалка создания нового счёта (из карточки-плейсхолдера) */}
      {createModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Новый счёт</h3>
            <div className="account-num-notice">
              <span className="icon">⚠️</span>
              <span><b>Важно:</b> указывайте номер именно вашего счёта с сервера — не придумывайте случайное число.</span>
            </div>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginTop: 0}}>
              Введите номер счёта с сервера (от 0 до 9999) и придумайте ему название.
            </p>
            <input 
              type="text" 
              inputMode="numeric"
              value={newAccountNum || ''} 
              onChange={(e) => setNewAccountNum(e.target.value.replace(/\D/g, '').slice(0, 4))} 
              placeholder="Номер счета (например: 1234)"
            />
            <input 
              type="text" 
              value={newAccountName || ''} 
              onChange={(e) => setNewAccountName(e.target.value)} 
              placeholder="Название (например: Копилка)"
            />
            {error && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{error}</div>}
            <div className="modal-actions">
              <button className="primary-btn" onClick={handleCreateAccount}>Создать</button>
              <button className="secondary-btn" onClick={() => { setCreateModal(false); setError(''); setNewAccountNum(''); setNewAccountName(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка настроек счета */}
      {settingsModal && currentAccount && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Настройки счета</h3>
            <input 
              type="text" 
              value={editAccountName || ''} 
              onChange={e => setEditAccountName(e.target.value)} 
              placeholder="Название счета"
            />
            <div className="color-picker">
              {COLORS.map(c => (
                <div 
                  key={c} 
                  className={`color-circle ${editAccountColor === c ? 'selected' : ''}`} 
                  style={{background: c}} 
                  onClick={() => setEditAccountColor(c)} 
                />
              ))}
            </div>

            <div style={{ marginTop: '15px' }}>
              <label style={{fontSize: '14px', fontWeight: 'bold'}}>Уникальный дизайн карты (фото)</label>
              <p style={{fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', marginBottom: '8px'}}>
                Замените фон карты своим уникальным изображением: {cardDesignPrice} сц за установку.
              </p>

              {designPreview && (
                <div style={{
                  width: '100%',
                  height: '0',
                  paddingTop: '62.5%',
                  boxSizing: 'border-box',
                  borderRadius: '12px',
                  backgroundImage: `url(${designPreview})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  marginBottom: '10px',
                  border: '1px solid var(--border)'
                }} />
              )}

              <input type="file" accept="image/*" onChange={handleDesignFileChange} style={{fontSize: '14px', padding: '8px'}} />

              {designError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{designError}</div>}

              <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                {designFile && (
                  <button className="primary-btn" style={{background: 'var(--success)'}} disabled={designSubmitting} onClick={handlePurchaseDesign}>
                    {designSubmitting ? 'Оплата...' : `Загрузить дизайн (${cardDesignPrice} сц)`}
                  </button>
                )}
                {currentAccount.card_image_url && !designFile && (
                  <button className="secondary-btn" onClick={handleRemoveDesign}>Убрать фото</button>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button className="primary-btn" onClick={handleUpdateAccount}>Сохранить</button>
              <button className="secondary-btn" onClick={() => { setSettingsModal(false); setDesignFile(null); setDesignError(''); }}>Отмена</button>
            </div>
            <button 
              className="action-btn" 
              style={{width: '100%', marginTop: '15px', color: 'var(--danger)', borderColor: 'var(--danger)'}}
              onClick={() => {
                setSettingsModal(false);
                setDeleteModal(true);
              }}
            >
              Удалить счет
            </button>
          </div>
        </div>
      )}

      {/* Модалка удаления счета */}
      {deleteModal && currentAccount && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Удаление счета</h3>
            {currentAccount.balance > 0 && ownTransferTargets.length > 0 ? (
              <div style={{
                fontSize: '14px', lineHeight: '1.4', color: 'var(--text-primary)',
                background: 'var(--warning-soft)', border: '1px solid var(--warning)',
                borderRadius: '10px', padding: '12px', marginBottom: '5px'
              }}>
                <b style={{color: 'var(--warning)'}}>Внимание!</b> На счету остались средства (<b>{currentAccount.balance} сц</b>).
                При удалении они будут автоматически переведены на счёт <b>{ownTransferTargets[0].account_name} #{ownTransferTargets[0].account_number}</b>.
              </div>
            ) : currentAccount.balance > 0 ? (
              <p style={{fontSize: '14px', color: 'var(--danger)', lineHeight: '1.4'}}>
                Внимание! На счету <b>{currentAccount.balance} сц</b>. Так как у вас нет других активных счетов, при удалении они сгорят.
              </p>
            ) : (
              <p style={{fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.4'}}>Счёт будет удалён без возможности восстановления.</p>
            )}
            <p style={{fontSize: '14px', marginBottom: '5px'}}>Для подтверждения введите:<br/><b>Удалить счет {currentAccount.account_number}</b></p>
            <input 
              type="text" 
              value={deleteConfirmText || ''} 
              onChange={e => setDeleteConfirmText(e.target.value)} 
              placeholder={`Удалить счет ${currentAccount.account_number}`}
            />
            <div className="modal-actions">
              <button 
                className="primary-btn" 
                style={{background: 'var(--danger)'}} 
                disabled={deleteConfirmText.trim().toLowerCase() !== `удалить счет ${currentAccount.account_number}`.toLowerCase()} 
                onClick={handleDeleteAccount}
              >
                Удалить
              </button>
              <button className="secondary-btn" onClick={() => {setDeleteModal(false); setDeleteConfirmText('');}}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка перевода */}
      {transferModal && currentAccount && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginBottom: '5px'}}>Перевод средств</h3>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '15px'}}>
              Списание со счета: <b>{currentAccount.account_name} #{currentAccount.account_number}</b>
            </p>
            
            {/* Переключатель категорий перевода */}
            <div className="transfer-tabs">
              <button
                type="button"
                className={`transfer-tab ${transferTab === 'external' ? 'active' : ''}`}
                onClick={() => { setTransferTab('external'); setTransferError(''); }}
              >На чужой счёт</button>
              <button
                type="button"
                className={`transfer-tab ${transferTab === 'internal' ? 'active' : ''}`}
                onClick={() => {
                  setTransferTab('internal');
                  setTransferError('');
                  // Сразу выбираем первый (самый старый) свой счёт-получатель — чтобы
                  // не заставлять игрока тапать по нему вручную, если он и так один или подходит.
                  setTransferOwnTarget(ownTransferTargets.length > 0 ? ownTransferTargets[0].account_number : null);
                }}
              >Между своими</button>
            </div>

            {transferTab === 'external' && (
              <div className="search-container">
                <input 
                  type="text" 
                  placeholder="Никнейм или номер счета получателя"
                  value={transferSearch || ''}
                  onChange={(e) => {
                    setTransferSearch(e.target.value);
                    setSelectedRecipient(null);
                  }}
                />
                {searchResults.length > 0 && !selectedRecipient && (
                  <div className="search-dropdown">
                    {searchResults.map((res, idx) => (
                      <div 
                        key={idx} 
                        className="search-item" 
                        onClick={() => {
                          setTransferSearch(res.display);
                          setSelectedRecipient(res);
                          setSearchResults([]);
                        }}
                      >
                        {res.type === 'nickname' ? <b>{res.display}</b> : <b>{res.display}</b>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {transferTab === 'internal' && (
              ownTransferTargets.length > 0 ? (
                <div className="own-accounts-grid">
                  {ownTransferTargets.map(acc => (
                    <button
                      type="button"
                      key={acc.account_number}
                      className={`own-account-btn ${transferOwnTarget === acc.account_number ? 'active' : ''}`}
                      onClick={() => setTransferOwnTarget(acc.account_number)}
                    >
                      <span className="own-account-dot" style={{ background: acc.account_color || DEFAULT_ACCOUNT_COLOR }} />
                      <span className="own-account-info">
                        <b>{acc.account_name}</b>
                        <span>#{acc.account_number} · {acc.balance} сц</span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p style={{fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center', margin: '10px 0'}}>
                  У вас нет других счетов для перевода.
                </p>
              )
            )}

            <input 
              type="number" 
              placeholder="Сумма перевода"
              value={transferAmount || ''}
              onChange={(e) => setTransferAmount(e.target.value)}
            />

            {transferTab === 'external' && (
              <input 
                type="text" 
                placeholder="Комментарий (необязательно)"
                value={transferComment || ''}
                onChange={(e) => setTransferComment(e.target.value)}
              />
            )}

            {transferError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{transferError}</div>}

            <div className="modal-actions">
              <button className="primary-btn" disabled={transferTab === 'internal' && ownTransferTargets.length === 0} onClick={handleTransfer}>Перевести</button>
              <button className="secondary-btn" onClick={() => {
                setTransferModal(false);
                setTransferError('');
                setTransferSearch('');
                setTransferAmount('');
                setTransferComment('');
                setSelectedRecipient(null);
                setTransferTab('external');
                setTransferOwnTarget(null);
              }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка вывода */}
      {withdrawModal && currentAccount && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginBottom: '5px'}}>Вывод средств</h3>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '15px'}}>
              Списание со счета: <b>{currentAccount.account_name} #{currentAccount.account_number}</b>
            </p>

            <input 
              type="number" 
              placeholder="Сумма вывода"
              value={withdrawAmount || ''}
              onChange={(e) => setWithdrawAmount(e.target.value)}
            />

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Куда вывести:</label>
            <select 
              value={withdrawDest} 
              onChange={e => setWithdrawDest(e.target.value)} 
            >
              <option value="ACCOUNT">На банковский счет сервера</option>
              {userData.postalAddress && <option value="MAIL">Наличкой на почту</option>}
            </select>

            {withdrawDest === 'ACCOUNT' && (
              <p style={{fontSize: '12px', color: 'var(--warning)', marginTop: '5px'}}>
                Внимание! Средства будут отправлены на одноименный номер счета (#{currentAccount.account_number}) на сервере MultiPunk.
              </p>
            )}
            {withdrawDest === 'MAIL' && (
              <p style={{fontSize: '12px', color: 'var(--warning)', marginTop: '5px'}}>
                Внимание! Убедитесь, что ваш почтовый адрес ({userData.postalAddress}) указан верно в профиле.
              </p>
            )}

            {withdrawError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{withdrawError}</div>}

            <div className="modal-actions">
              <button className="primary-btn" onClick={handleWithdraw}>Подать заявление</button>
              <button className="secondary-btn" onClick={() => {
                setWithdrawModal(false);
                setWithdrawError('');
                setWithdrawAmount('');
                setWithdrawDest('ACCOUNT');
              }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка инвойсов */}
      {invoicesModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Ваши инвойсы</h3>
            {invoices.length === 0 ? (
              <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>У вас нет неоплаченных инвойсов.</p>
            ) : (
              invoices.map(inv => (
                <div key={inv.id} style={{border: '1px solid var(--border)', borderRadius: '8px', padding: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end'}}>
                  <div>
                    <span style={{fontWeight: 'bold', color: 'var(--danger)', fontSize: '16px'}}>{inv.amount} сц</span>
                    <p style={{margin: '4px 0', fontSize: '12px', color: 'var(--text-secondary)'}}><b>Счет зачисления:</b> {inv.destination_account ? `#${inv.destination_account}` : 'услуга Azon'}</p>
                    <p style={{margin: '4px 0', fontSize: '12px', color: 'var(--text-secondary)'}}><b>Причина:</b> {inv.reason}</p>
                    <p style={{margin: 0, fontSize: '11px', color: 'var(--text-secondary)'}}>Спишется с текущего выбранного счёта</p>
                  </div>
                  <button className="primary-btn" style={{background: 'var(--success)', padding: '6px 12px', fontSize: '12px', width: 'auto', flexShrink: 0, marginLeft: '10px'}} onClick={() => handlePayInvoice(inv.id)}>Оплатить</button>
                </div>
              ))
            )}
            <button className="secondary-btn" style={{marginTop: '15px'}} onClick={() => setInvoicesModal(false)}>Закрыть</button>
          </div>
        </div>
      )}

      {/* Модалка пополнения */}
      {depositModal && currentAccount && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginBottom: '5px'}}>Пополнение счета</h3>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '15px'}}>
              <span style={{color: 'var(--accent-dark)'}}>Прикрепление скриншота ускорит обработку пополнения.</span>
            </p>

            <input 
              type="number" 
              placeholder="Сумма пополнения"
              value={depositAmount || ''}
              onChange={(e) => setDepositAmount(e.target.value)}
            />

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Откуда перевели:</label>
            <select 
              value={depositSourceType} 
              onChange={e => setDepositSourceType(e.target.value)} 
            >
              <option value="ACCOUNT">Со счета MP</option>
              <option value="MAIL">С почты</option>
            </select>

            <input 
              type="text" 
              placeholder={depositSourceType === 'ACCOUNT' ? "Номер вашего счета MP" : "Ваш адрес почты"}
              value={depositSourceValue || ''}
              onChange={(e) => {
                let val = e.target.value;
                if (depositSourceType === 'ACCOUNT') {
                  val = val.replace(/\D/g, '').slice(0, 4);
                }
                setDepositSourceValue(val);
              }}
            />

            {depositSourceType === 'ACCOUNT' && (
              <div
                onClick={() => copyToClipboard('2500')}
                style={{marginTop: '8px', padding: '12px 14px', cursor: 'pointer', background: 'var(--accent-soft)', border: '1.5px dashed var(--accent)', borderRadius: '12px', textAlign: 'center'}}
              >
                <div style={{fontSize: '12px', fontWeight: 700, color: 'var(--accent-dark)', marginBottom: '2px'}}>Счёт для перевода</div>
                <div style={{fontSize: '22px', fontWeight: 800, color: 'var(--accent-dark)', letterSpacing: '1px'}}>2500</div>
                <div style={{fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px'}}>нажмите, чтобы скопировать</div>
              </div>
            )}
            {depositSourceType === 'MAIL' && (
              <div
                onClick={() => copyToClipboard('Azon')}
                style={{marginTop: '8px', padding: '12px 14px', cursor: 'pointer', background: 'var(--accent-soft)', border: '1.5px dashed var(--accent)', borderRadius: '12px', textAlign: 'center'}}
              >
                <div style={{fontSize: '12px', fontWeight: 700, color: 'var(--accent-dark)', marginBottom: '2px'}}>Почта для перевода</div>
                <div style={{fontSize: '22px', fontWeight: 800, color: 'var(--accent-dark)'}}>Azon</div>
                <div style={{fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px'}}>нажмите, чтобы скопировать</div>
              </div>
            )}

            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Скриншот перевода (необязательно):</label>
            <input 
              type="file" 
              accept="image/*"
              onChange={handleDepositFileChange}
              style={{fontSize: '14px', padding: '8px'}}
            />

            {depositError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{depositError}</div>}

            <div className="modal-actions">
              <button className="primary-btn" onClick={handleDeposit}>Подать заявление</button>
              <button className="secondary-btn" onClick={() => {
                setDepositModal(false);
                setDepositError('');
                setDepositAmount('');
                setDepositSourceValue('');
                setDepositProof('');
              }}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Bank;
